import { useState, useLayoutEffect } from 'react';

/**
 * Hook to get corrected viewport dimensions for WKWebView
 * 
 * WKWebView has a known bug where it reports PORTRAIT dimensions
 * even when the device is in LANDSCAPE for the first few frames.
 * 
 * This hook detects this "lie" by checking the device orientation angle
 * (which IS correct) and swaps the dimensions if they don't match.
 */

interface CorrectedDimensions {
  width: number;
  height: number;
  isLying: boolean;
  isLandscape: boolean;
}

// Helper to get actual orientation from device
const getDeviceOrientation = (): { isLandscape: boolean; angle: number } => {
  // Modern API (screen.orientation)
  if (window.screen?.orientation?.angle !== undefined) {
    const angle = window.screen.orientation.angle;
    return {
      angle,
      isLandscape: angle === 90 || angle === 270 || angle === -90,
    };
  }
  
  // Legacy iOS API (window.orientation)
  // @ts-ignore - window.orientation is deprecated but still works on iOS
  if (typeof window.orientation !== 'undefined') {
    // @ts-ignore
    const angle = window.orientation as number;
    return {
      angle,
      isLandscape: Math.abs(angle) === 90,
    };
  }
  
  // Fallback: guess from dimensions (not reliable for detecting lies)
  return {
    angle: 0,
    isLandscape: window.innerWidth > window.innerHeight,
  };
};

const getDimensions = (): CorrectedDimensions => {
  let width = window.innerWidth;
  let height = window.innerHeight;
  
  const { isLandscape, angle } = getDeviceOrientation();
  
  // DETECT THE LIE:
  // If device orientation says Landscape (90°), but Width < Height...
  // The WebView is lying to us. Swap the dimensions manually.
  const isLying = isLandscape && width < height;
  
  if (isLying) {
    // Force the largest dimension to Width (landscape)
    // Force the smallest dimension to Height
    return {
      width: Math.max(width, height),
      height: Math.min(width, height),
      isLying: true,
      isLandscape: true,
    };
  }
  
  return {
    width,
    height,
    isLying: false,
    isLandscape,
  };
};

export function useCorrectedDimensions(): CorrectedDimensions {
  const [dimensions, setDimensions] = useState<CorrectedDimensions>(getDimensions);

  useLayoutEffect(() => {
    const handleResize = () => {
      setDimensions(getDimensions());
    };

    // Listen to both resize and orientation change
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    // Also listen to visualViewport if available
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    // Check immediately in case initial state was wrong
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  return dimensions;
}

// Utility function for one-time check (non-hook)
export const getCorrectedDimensions = getDimensions;

